// hooks/usePlayerStore.ts
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { DevoChapter } from "~/lib/audioChapters";
import type { Playable } from "~/lib/audioSequence";
import { getLoadSequenceId } from "~/lib/audioSequencePlan";
import { EPISODE_COMPLETE_THRESHOLD } from "~/lib/constants";
import { getEpisodePlaybackProgress } from "~/lib/episode.utils";
import type { AudioChapterType } from "~/lib/scheduledAudio";
import type { ProgressByEpisodeItem } from "~/lib/series.utils";
import type { GetSeriesBySlugQueryResult, ScriptureReference } from "~/sanity/sanity.types";
import { ProgressClientService } from "~/services/progress.client";

type ManagedExternalLink = {
  permissions?: Array<unknown> | null;
  startDate?: string | null;
  expirationDate?: string | null;
  url?: string | null;
  title?: string | null;
  subtitle?: string | null;
  imageUrl?: string | null;
};

export type RelatedResourceLink = ManagedExternalLink;
export type ResourceTabEntry = ManagedExternalLink;

export type MeditationMoment = {
  tracks: Array<AudioTrack>;
  scriptureReferences?: Devo["scriptureReferences"] | null;
  hasPlayableAudio: boolean;
};

export type AdditionalAudioAsset = {
  source: string;
  title: string | null;
  chapterType?: AudioChapterType | null;
  chapterSectionTitle?: string | null;
};

export type BookendAsset = AdditionalAudioAsset;

export type Devo = {
  id: string;
  date: string | null;
  startShowingDate?: string | null;
  title: string | null;
  subtitle: string | null;
  scriptureReferences?: Array<{
    book?: ScriptureReference["book"] | null;
    startChapter?: number | null;
    startVerse?: number | null;
    endChapter?: number | null;
    endVerse?: number | null;
    esvText?: string | null;
  }> | null;
  contributors: Array<{
    _id: string;
    name: string | null;
    slug: {
      current: string | null;
    } | null;
  }> | null;
  thumbnail: string | null;
  thumbnailAlt: string | null;
  transcript: string | null;
  tags?: Array<string>;
  tracks: Array<AudioTrack>;
  fallbackTracks?: Array<AudioTrack>;
  compositeAudioTimeline?: CompositeAudioTimeline;
  songs: Array<{
    _id: string;
    title: string | null;
    slug: {
      current: string | null;
    } | null;
  }>;
  durationSeconds: number | null;
  readInAssets?: Array<AdditionalAudioAsset>;
  readOutAssets?: Array<AdditionalAudioAsset>;
  /** @deprecated Use readOutAssets. Retained for zero/one-read-out compatibility. */
  readOutAssetSource?: string | null;
  /** @deprecated Use readOutAssets. Retained for zero/one-read-out compatibility. */
  readOutAssetTitle?: string | null;
  shouldRedirectOnComplete: boolean;
  lyricsBlock?: Array<EditorBlock> | null;
  trailerTrackCount?: number;
  outroTrackCount?: number;
  fallbackTrailerTrackCount?: number;
  fallbackOutroTrackCount?: number;
  resourceTab?: Array<ResourceTabEntry> | null;
  externalResourceLinks?: Array<RelatedResourceLink | null> | null;
  series: Array<Series> | null;
  enableAutoScroll?: boolean | null;
  deepLink: string;
  sharePercentage: number | null;
  canAutoplayForUser?: boolean;
  outroVideoVimeoId?: string | null;
  meditationMoment?: MeditationMoment | null;
  chapters?: Array<DevoChapter> | null;
};

export type CompositeAudioTimeline = {
  mainStartSeconds: number;
  mainEndSeconds: number;
};

export function getDevoPlaybackIdentity(devo: Devo): string {
  return JSON.stringify({
    episodeId: devo.id,
    sequenceId: getLoadSequenceId(devo.tracks, devo.fallbackTracks),
    compositeAudioTimeline: devo.compositeAudioTimeline ?? null,
    trailerTrackCount: devo.trailerTrackCount ?? 0,
    outroTrackCount: devo.outroTrackCount ?? 0,
    fallbackTrailerTrackCount: devo.fallbackTrailerTrackCount ?? 0,
    fallbackOutroTrackCount: devo.fallbackOutroTrackCount ?? 0,
  });
}

export type Series = {
  _id: string;
  name?: string | null;
  description?: string | null;
  slug?: {
    current?: string | null;
  } | null;
  logomarkUrl?: string | null;
  images?: Array<Images> | null;
  scriptureReferences?: Array<{
    book?: string | null;
    startChapter?: number | null;
    startVerse?: number | null;
    esvText?: string | null;
  }> | null;
  contributors?: Array<{
    _id?: string;
    name?: string | null;
    slug?: {
      current?: string | null;
    } | null;
    biography?: string | null;
    images?: Array<Images> | null;
  }> | null;
  resourceTab?: Array<ResourceTabEntry> | null;
  externalResourceLinks?: Array<RelatedResourceLink | null> | null;
  shareBackdropImages?: NonNullable<GetSeriesBySlugQueryResult>["shareBackdropImages"];
};

export type Images = {
  imageType?:
    | "Background"
    | "Feature"
    | "Hero"
    | "Thumbnail"
    | "Video Series Poster"
    | "Logomark"
    | "Icon"
    | "Lock Screen"
    | null;
  imageName?: string | null;
  imageUrl?: string | null;
};

export type EditorBlock = {
  children?:
    | Array<{
        marks?: Array<string>;
        text?: string;
        _type: "span";
        _key: string;
      }>
    | null
    | undefined;
  style?: "blockquote" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "normal";
  listItem?: "bullet" | "number";
  markDefs?:
    | Array<{
        href?: string;
        _type: "link";
        _key: string;
      }>
    | null
    | undefined;
  level?: number;
  _type: "block";
  _key: string;
};

export type AudioTrack = {
  source: string;
  fallbackSource?: string | null;
};

export type DevoCompletionStatus = {
  devoId: string;
  completed: boolean;
  progress: number;
  duration: number;
};

export type PlayerStore = {
  // UI State
  devo?: Devo;
  lastEpisodeId?: string;
  hasRedirected: boolean;
  playerExpanded: boolean;
  metadataLoaded: boolean;
  miniPlayerVisible: boolean;
  meditationCompletionPromptShownByEpisode: Record<string, boolean>;

  // Trailer/Progress tracking
  trailerOffsetSeconds: number;
  completions: Record<string, DevoCompletionStatus>;
  progressData: Array<ProgressByEpisodeItem>;
  progressLastSent: number;

  // Actions
  setDevo: (devo: Devo) => void;
  setLastEpisodeId: (id: string) => void;
  setHasRedirected: (redirected: boolean) => void;
  setPlayerExpanded: (expanded: boolean) => void;
  setTrailerOffsetSeconds: (seconds: number) => void;
  setProgressData: (progressData: Array<ProgressByEpisodeItem>) => void;
  setMetadataLoaded: (loaded: boolean) => void;
  setMiniPlayerVisible: (visible: boolean) => void;
  setProgressLastSent: (progressLastSent: number) => void;
  markMeditationCompletionPromptShown: (devoId: string) => void;
  dismissActivePlayback: () => void;
  restartEpisodeSession: (devoId: string) => void;
  reset: () => void;

  // Completion tracking
  updateCompletion: (devoId: string, progress: number, duration: number) => void;
  markCompleted: (devoId: string) => void;
};

let lastSentProgress = 0;
let progressInterval: NodeJS.Timeout | null = null;

type EpisodeProgressUpdateArgs = {
  progress: number;
  duration: number;
  devoId: string;
  playable?: Playable | null;
  trailerTrackCount?: number;
  outroTrackCount?: number;
  totalTrackCount?: number;
  mainStartSeconds?: number;
  mainEndSeconds?: number;
};

function getNormalizedProgressUpdate(
  args: EpisodeProgressUpdateArgs,
): Pick<EpisodeProgressUpdateArgs, "progress" | "duration"> {
  const {
    progress,
    duration,
    playable,
    trailerTrackCount = 0,
    outroTrackCount = 0,
    totalTrackCount = 0,
    mainStartSeconds,
    mainEndSeconds,
  } = args;

  if (!playable) {
    return { progress, duration };
  }

  const playbackProgress = getEpisodePlaybackProgress({
    progress,
    duration,
    playable,
    trailerTrackCount,
    outroTrackCount,
    totalTrackCount,
    mainStartSeconds,
    mainEndSeconds,
  });

  return {
    progress: playbackProgress.mainProgressSeconds,
    duration: playbackProgress.mainDuration,
  };
}

async function sendProgressToBackend(progress: number, duration: number, devoId: string) {
  if (duration === 0) return;
  if (Math.abs(progress - lastSentProgress) / duration > 0.01) {
    lastSentProgress = progress;
    try {
      // Convert absolute seconds progress to percentage of episode
      const percentage = Math.max(0, Math.min(100, Math.floor((progress / duration) * 100)));
      await ProgressClientService.logListenProgress(devoId, percentage);
    } catch (error) {
      console.error("Error sending progress to backend:", error);
    }
  }
}

/**
 * Update devo progress from seek actions
 */
export function triggerProgressUpdateAndReset(args: EpisodeProgressUpdateArgs) {
  if (progressInterval) {
    clearInterval(progressInterval);
    progressInterval = null;
  }
  const normalized = getNormalizedProgressUpdate(args);
  void sendProgressToBackend(normalized.progress, normalized.duration, args.devoId);
}
export const usePlayerStore = create<PlayerStore>()(
  persist(
    (set, get) => ({
      // Initial state
      playerExpanded: false,
      hasRedirected: false,
      metadataLoaded: false,
      miniPlayerVisible: false,
      meditationCompletionPromptShownByEpisode: {},
      trailerOffsetSeconds: 0,
      completions: {},
      progressData: [],
      progressLastSent: 0,

      // Actions
      setDevo: (devo) => {
        const current = get();
        const playbackChanged =
          !current.devo || getDevoPlaybackIdentity(current.devo) !== getDevoPlaybackIdentity(devo);
        set({
          devo,
          metadataLoaded: playbackChanged ? false : current.metadataLoaded,
          hasRedirected: false,
          miniPlayerVisible: true,
        });
      },

      setLastEpisodeId: (id) => {
        set({ lastEpisodeId: id });
      },

      setHasRedirected: (hasRedirected) => {
        set({ hasRedirected, playerExpanded: false });
      },

      setPlayerExpanded: (playerExpanded) => {
        set({ playerExpanded });
      },

      setTrailerOffsetSeconds: (seconds) => {
        set({ trailerOffsetSeconds: seconds });
      },

      setProgressData: (progressData) => {
        set({ progressData });
      },

      setMetadataLoaded: (loaded) => {
        set({ metadataLoaded: loaded });
      },

      setMiniPlayerVisible: (visible) => {
        set({ miniPlayerVisible: visible });
      },

      setProgressLastSent: (progressLastSent) => {
        set({ progressLastSent });
      },
      markMeditationCompletionPromptShown: (devoId) => {
        const { meditationCompletionPromptShownByEpisode } = get();
        if (meditationCompletionPromptShownByEpisode[devoId]) {
          return;
        }

        set({
          meditationCompletionPromptShownByEpisode: {
            ...meditationCompletionPromptShownByEpisode,
            [devoId]: true,
          },
        });
      },
      dismissActivePlayback: () => {
        set({
          devo: undefined,
          lastEpisodeId: undefined,
          playerExpanded: false,
          hasRedirected: false,
          metadataLoaded: false,
          miniPlayerVisible: false,
          trailerOffsetSeconds: 0,
          progressLastSent: 0,
        });
      },
      restartEpisodeSession: (devoId) => {
        const { completions, progressData, meditationCompletionPromptShownByEpisode } = get();
        const completionState = completions[devoId];

        const { [devoId]: _ignoredPromptFlag, ...remainingPromptFlags } = meditationCompletionPromptShownByEpisode;

        set({
          completions: completionState
            ? {
                ...completions,
                [devoId]: {
                  ...completionState,
                  completed: false,
                  progress: 0,
                },
              }
            : completions,
          progressData: progressData.map((item) =>
            item.cmsId === devoId
              ? {
                  ...item,
                  progressPercentage: 0,
                  completedAtUtc: undefined,
                }
              : item,
          ),
          meditationCompletionPromptShownByEpisode: remainingPromptFlags,
          progressLastSent: 0,
        });
      },
      reset: () => {
        set({
          devo: undefined,
          lastEpisodeId: undefined,
          playerExpanded: false,
          hasRedirected: false,
          metadataLoaded: false,
          miniPlayerVisible: false,
          meditationCompletionPromptShownByEpisode: {},
          trailerOffsetSeconds: 0,
          completions: {},
          progressData: [],
        });
      },

      updateCompletion: (devoId, progress, duration) => {
        const { completions } = get();
        const existing = completions[devoId] || {
          devoId,
          completed: false,
          progress: 0,
          duration: 0,
        };

        const isCompleted = existing.completed || (duration > 0 && progress / duration > EPISODE_COMPLETE_THRESHOLD);

        set({
          completions: {
            ...completions,
            [devoId]: {
              devoId,
              completed: isCompleted,
              progress,
              duration,
            },
          },
        });
      },

      markCompleted: (devoId) => {
        const { completions } = get();
        const existing = completions[devoId];
        if (existing) {
          set({
            completions: {
              ...completions,
              [devoId]: {
                ...existing,
                completed: true,
              },
            },
          });
        }
      },
    }),
    {
      name: "twi:player-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) =>
        ({
          completions: state.completions,
          progressData: state.progressData,
          lastEpisodeId: state.lastEpisodeId ?? state.devo?.id,
        }) as Partial<PlayerStore>,
      // Never hydrate authorization-derived audio URLs from older persisted
      // player snapshots. Only durable client intent and progress survive reloads.
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<PlayerStore>;
        return {
          ...currentState,
          completions: persisted.completions ?? currentState.completions,
          progressData: persisted.progressData ?? currentState.progressData,
          lastEpisodeId: persisted.lastEpisodeId ?? currentState.lastEpisodeId,
        };
      },
    },
  ),
);
